import { PhoneCallbackOutlined } from '@mui/icons-material';
import { IconButton, Tooltip } from '@mui/material';
import type { TranslationKey } from '@/i18n/translation-key';
import type { FlowContributorStatusIconColor } from '@/utils/contributor-extraction.utils';
import { contributorRemoteSpeakerStatusIconTooltip } from '@/components/events/detail/tabs/common/contributors/contributor-item-action-labels';

const statusIconButtonSx = {
  '& > svg': {
    fontSize: '1.5rem',
    width: '1.5rem',
    height: '1.5rem',
  },
} as const;

/** Keep layout identical to clickable IconButtons when the status icon is display-only. */
const nonInteractiveIconButtonSx = {
  ...statusIconButtonSx,
  'cursor': 'default',
  '&:hover': { backgroundColor: 'transparent' },
} as const;

function toMuiIconColor(color: FlowContributorStatusIconColor | null): 'primary' | 'info' | 'warning' | 'success' {
  if (color === 'primary') return 'primary';
  if (color === 'warning') return 'warning';
  if (color === 'success') return 'success';
  return 'info';
}

export type ContributorEventRemoteSpeakerHeaderIconProps = {
  t: (key: TranslationKey) => string;
  showIcon: boolean;
  iconColor: FlowContributorStatusIconColor | null;
  onPendingClick?: () => void;
};

export function ContributorEventRemoteSpeakerHeaderIcon({
  t,
  showIcon,
  iconColor,
  onPendingClick,
}: ContributorEventRemoteSpeakerHeaderIconProps) {
  if (!showIcon) return null;

  const pendingClickable = iconColor === 'primary' && Boolean(onPendingClick);
  const tooltip = contributorRemoteSpeakerStatusIconTooltip(t, {
    slotIsRemote: true,
    color: iconColor,
    context: 'contributorRow',
  });
  const icon = (
    <PhoneCallbackOutlined fontSize="small" color={toMuiIconColor(iconColor)} sx={{ transform: 'scaleX(-1)' }} />
  );

  return (
    <Tooltip title={tooltip} placement="left" disableInteractive>
      <IconButton
        size="small"
        disableRipple={!pendingClickable}
        tabIndex={pendingClickable ? undefined : -1}
        aria-label={tooltip}
        data-testid="contributor-header-remote-speaker"
        onClick={(event) => {
          event.stopPropagation();
          if (pendingClickable) {
            onPendingClick?.();
          }
        }}
        sx={pendingClickable ? statusIconButtonSx : nonInteractiveIconButtonSx}
      >
        {icon}
      </IconButton>
    </Tooltip>
  );
}
